import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronRight, FaPlus } from "react-icons/fa";
import {
  AccessScopeDto,
  useCreateDepartmentMutation,
  useListAccessGrantsQuery,
  useListDepartmentsQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { DepartmentAccess } from "./DepartmentAccess";
import { SettingsCard } from "./SettingsCard";

export const Departments = () => {
  const authState = useLoggedInWithOrgContextState();
  const orgId = authState.selectedOrg.id;
  const { t } = useTranslation();
  const displayer = useApiError();
  const toast = useToast();

  const { data, isLoading } = useListDepartmentsQuery({ orgId });
  const { data: grantData } = useListAccessGrantsQuery({ orgId });
  const [createDepartment, { isLoading: isCreating }] =
    useCreateDepartmentMutation();

  const [newName, setNewName] = useState("");
  const [openedId, setOpenedId] = useState<string | null>(null);

  const managesDepartments = authState.selectedOrg.canManageUsers;
  const departments = data?.departments ?? [];
  const opened = departments.find((department) => department.id === openedId);

  const peopleWithAccess = useMemo(() => {
    const byDepartment = new Map<string, Set<string>>();
    (grantData?.grants ?? []).forEach((grant) => {
      if (grant.scope !== AccessScopeDto.Department || !grant.departmentId)
        return;
      const people = byDepartment.get(grant.departmentId) ?? new Set<string>();
      people.add(grant.personId);
      byDepartment.set(grant.departmentId, people);
    });
    return byDepartment;
  }, [grantData]);

  const create = async () => {
    try {
      await createDepartment({
        orgId,
        departmentNameRequest: { name: newName },
      }).unwrap();
      toast({ title: t("Department created!"), status: "success" });
      setNewName("");
    } catch (e) {
      displayer.trigger(e);
    }
  };

  if (opened)
    return (
      <DepartmentAccess department={opened} onBack={() => setOpenedId(null)} />
    );

  return (
    <>
      <Text fontSize="sm" color="smMuted" mt="5">
        {t(
          "Everything you create belongs to a department. Add more to give people access in one of them only.",
        )}
      </Text>
      <SettingsCard
        title={t("Departments")}
        actions={
          managesDepartments ? (
            <Flex alignItems="center" gap="2">
              <Input
                size="sm"
                width="220px"
                backgroundColor="smBackground"
                placeholder={t("Name of the new department") ?? ""}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button
                size="sm"
                leftIcon={<FaPlus />}
                colorScheme="teal"
                isDisabled={!newName.trim()}
                isLoading={isCreating}
                onClick={create}
              >
                {t("New department")}
              </Button>
            </Flex>
          ) : undefined
        }
        inset={false}
      >
        {/* borderBottomColor, not borderColor: cssstyle cannot parse the border shorthand Chakra's
          table theme emits, and jsdom throws when a test computes styles. */}
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th pl="5" borderBottomColor="smBorderSubtle">
                {t("Name")}
              </Th>
              <Th
                isNumeric
                borderBottomColor="smBorderSubtle"
                whiteSpace="nowrap"
              >
                {t("With access")}
              </Th>
              <Th isNumeric borderBottomColor="smBorderSubtle">
                {t("Sourcing events")}
              </Th>
              <Th isNumeric borderBottomColor="smBorderSubtle">
                {t("Contracts")}
              </Th>
              <Th pr="5" borderBottomColor="smBorderSubtle" />
            </Tr>
          </Thead>
          <Tbody>
            {departments.map((department) => (
              <Tr
                key={department.id}
                cursor="pointer"
                _hover={{ backgroundColor: "smBackgroundSecondary" }}
                onClick={() => setOpenedId(department.id)}
              >
                <Td pl="5" borderBottomColor="smBorderSubtle">
                  <Flex alignItems="baseline" columnGap="2">
                    <Text fontSize="sm" fontWeight="semibold">
                      {department.name}
                    </Text>
                    {department.isDefault && (
                      <Text fontSize="xs" color="smMuted">
                        {t("default")}
                      </Text>
                    )}
                    {!department.active && (
                      <Badge colorScheme="orange">{t("Retired")}</Badge>
                    )}
                  </Flex>
                </Td>
                <Td
                  isNumeric
                  borderBottomColor="smBorderSubtle"
                  fontSize="sm"
                  color="smMuted"
                >
                  {peopleWithAccess.get(department.id)?.size ?? 0}
                </Td>
                <Td
                  isNumeric
                  borderBottomColor="smBorderSubtle"
                  fontSize="sm"
                  color="smMuted"
                >
                  {department.events}
                </Td>
                <Td
                  isNumeric
                  borderBottomColor="smBorderSubtle"
                  fontSize="sm"
                  color="smMuted"
                >
                  {department.contracts}
                </Td>
                <Td pr="5" borderBottomColor="smBorderSubtle" textAlign="right">
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="teal"
                    rightIcon={<FaChevronRight />}
                  >
                    {t("Edit")}
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {!departments.length && (
          <Box px="5" py="6">
            <Text fontSize="sm" color="smMuted">
              {isLoading
                ? `${t("Loading")}...`
                : t("No departments have been added")}
            </Text>
          </Box>
        )}
      </SettingsCard>
    </>
  );
};
